import { type FC, useState } from "react";
import { EyeOff, Eye, Info } from "lucide-react";
import { enqueueSnackbar } from "notistack";

import { Button } from "@/shared/components/ui/button";
import { FieldError } from "@/shared/components/ui/FieldError";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import { useUpdateBeneficiaries } from "@/shared/hooks/useUpdateBeneficiaries";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { OWNERSHIP_TYPES } from "@/shared/constants/ownershipTypes";
import { resolveBalance } from "@/shared/utils/balanceEstimates";
import { currencyInput } from "@/shared/utils/currencyInput";
import { BalanceEstimateSection } from "@/shared/components/EstateItems/BalanceEstimateSection";
import { PeopleSelector } from "@/shared/components/PeopleSelector";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

import { ACCOUNT_TYPE_OPTIONS, CO_OWNER_OWNERSHIP_TYPES } from "./FinancialAccountForm";

const BENEFICIARY_OWNERSHIP_TYPES = new Set([
  "revocable_trust",
  "irrevocable_trust",
  "pod_tod",
  "beneficiary_designation",
  "custodial_decedent",
  "specific_bequest",
]);

export const ManualAccountEntry: FC<FlowCustomComponentProps> = ({ goToScreen }) => {
  const { person } = useAppContext();
  const estateId = person?.estate_id;
  const firstName = person?.first_name ?? "your loved one";

  const { addItem } = useEstateItems(estateId ?? null);
  const { addBeneficiaryAsync } = useUpdateBeneficiaries();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedAccountType, setSelectedAccountType] = useState("");
  const [institution, setInstitution] = useState("");
  const [value, setValue] = useState("");
  const [accountIdentifier, setAccountIdentifier] = useState("");
  const [identifierMasked, setIdentifierMasked] = useState(false);
  const [balanceUnknown, setBalanceUnknown] = useState(false);
  const [estimateRange, setEstimateRange] = useState("");
  const [ownershipType, setOwnershipType] = useState("");
  const [coOwners, setCoOwners] = useState("");
  const [beneficiaryIds, setBeneficiaryIds] = useState<number[]>([]);

  const selectedOption = ACCOUNT_TYPE_OPTIONS.find((o) => o.itemType === selectedAccountType);
  const showCoOwners = ownershipType && CO_OWNER_OWNERSHIP_TYPES.has(ownershipType);
  const showBeneficiaries = ownershipType && BENEFICIARY_OWNERSHIP_TYPES.has(ownershipType);

  const { handleChange: handleValueChange, displayValue } = currencyInput(value, setValue);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const handleSubmit = async () => {
    const errors: Record<string, string> = {};
    if (!selectedAccountType) errors.accountType = "Please select an account type";
    const hasValue = balanceUnknown ? !!estimateRange : !!(value && parseFloat(value) > 0);
    if (!hasValue) errors.balance = "Please enter a balance or estimate";
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    if (!estateId || !selectedOption) return;

    setIsSubmitting(true);
    try {
      const metaFields: Record<string, unknown> = {};
      if (institution.trim()) metaFields.institution = institution.trim();
      if (showCoOwners && coOwners.trim()) metaFields.co_owners = coOwners.trim();
      if (accountIdentifier.trim()) metaFields.account_last4 = accountIdentifier.trim();
      const metadata = Object.keys(metaFields).length > 0 ? metaFields : undefined;
      const resolvedBalance = resolveBalance(balanceUnknown, estimateRange, value);
      const ownershipValue = ownershipType || undefined;

      const identifiers = accountIdentifier.trim()
        ? { account_last4: accountIdentifier.trim() }
        : undefined;

      const created = await addItem({
        estate_id: estateId,
        classification: "asset",
        category: selectedOption.category,
        item_type: selectedOption.itemType,
        item_name: institution.trim() || selectedOption.itemType,
        balance: resolvedBalance,
        is_estimated: balanceUnknown,
        balance_estimate_range: balanceUnknown ? estimateRange : null,
        ownership: ownershipValue,
        ...(metadata ? { metadata } : {}),
        ...(identifiers ? { identifiers } : {}),
      });

      if (beneficiaryIds.length > 0 && created?.id) {
        const results = await Promise.allSettled(
          beneficiaryIds.map((contactId) =>
            addBeneficiaryAsync({ estate_item_id: created.id, contact_id: contactId }),
          ),
        );
        const failed = results.filter((r) => r.status === "rejected").length;
        if (failed > 0) {
          enqueueSnackbar(
            `${failed} beneficiar${failed === 1 ? "y" : "ies"} couldn't be saved. Please try again.`,
            {
              variant: "warning",
            },
          );
        }
      }

      enqueueSnackbar("Account added", { variant: "success" });
      goToScreen("review_accounts");
    } catch {
      enqueueSnackbar("Couldn't add account. Please try again.", { variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-8 rounded-xl border border-primary bg-white p-6">
        <div className="space-y-2">
          <Label className="text-sm font-medium" required>
            Account type
          </Label>
          <Select
            value={selectedAccountType}
            onValueChange={(val) => {
              setSelectedAccountType(val);
              setInstitution("");
              setOwnershipType("");
              setCoOwners("");
              setBeneficiaryIds([]);
              setFieldErrors((prev) => {
                const next = { ...prev };
                delete next.accountType;
                return next;
              });
            }}
          >
            <SelectTrigger className={fieldErrors.accountType ? "border-error" : ""}>
              <SelectValue placeholder="Select account type" />
            </SelectTrigger>
            <SelectContent>
              {ACCOUNT_TYPE_OPTIONS.map((opt) => (
                <SelectItem key={opt.itemType} value={opt.itemType}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError message={fieldErrors.accountType} />
        </div>

        <div className="space-y-2">
          <Label className="text-sm font-medium">Institution</Label>
          <Input
            placeholder="e.g., Chase, Fidelity, Vanguard"
            value={institution}
            onChange={(e) => setInstitution(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium" required>
              Balance
            </Label>
            <div className="relative">
              <span className="absolute inset-y-0 left-3 flex items-center text-sm text-secondary">
                $
              </span>
              <Input
                type="text"
                placeholder="0.00"
                value={displayValue}
                onChange={(e) => {
                  handleValueChange(e);
                  setFieldErrors((prev) => {
                    const next = { ...prev };
                    delete next.balance;
                    return next;
                  });
                }}
                className={`pl-7 disabled:bg-subtle disabled:text-tertiary ${fieldErrors.balance ? "border-error" : ""}`}
                disabled={balanceUnknown}
              />
            </div>
            <FieldError message={fieldErrors.balance} />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              Last 4 of account number <span className="text-secondary">(optional)</span>
            </Label>
            <div className="relative">
              <Input
                value={
                  identifierMasked && accountIdentifier
                    ? "\u2022\u2022\u2022\u2022"
                    : accountIdentifier
                }
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, "");
                  setIdentifierMasked(false);
                  setAccountIdentifier(digits);
                }}
                onFocus={() => setIdentifierMasked(false)}
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="e.g., 7890"
                maxLength={4}
                className="pr-9"
              />
              {accountIdentifier && (
                <button
                  type="button"
                  onClick={() => setIdentifierMasked((m) => !m)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-tertiary hover:text-primary"
                  aria-label={identifierMasked ? "Show number" : "Hide number"}
                >
                  {identifierMasked ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </button>
              )}
            </div>
          </div>
        </div>

        <BalanceEstimateSection
          balanceUnknown={balanceUnknown}
          estimateRange={estimateRange}
          onBalanceUnknownChange={(checked) => {
            setBalanceUnknown(checked);
            setFieldErrors((prev) => {
              const next = { ...prev };
              delete next.balance;
              return next;
            });
            if (!checked) {
              setEstimateRange("");
            } else {
              setValue("");
            }
          }}
          onEstimateRangeChange={setEstimateRange}
        />

        <div className="space-y-2">
          <Label className="flex items-center gap-1 text-sm font-medium">
            Ownership type <span className="text-secondary">(optional)</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3.5 w-3.5 text-tertiary" />
                </TooltipTrigger>
                <TooltipContent>
                  How the account was owned determines who inherits it and how it transfers.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </Label>
          <Select
            value={ownershipType}
            onValueChange={(val) => {
              setOwnershipType(val);
              if (!CO_OWNER_OWNERSHIP_TYPES.has(val)) setCoOwners("");
              if (!BENEFICIARY_OWNERSHIP_TYPES.has(val)) setBeneficiaryIds([]);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select ownership type" />
            </SelectTrigger>
            <SelectContent>
              {OWNERSHIP_TYPES.map((t) => (
                <SelectItem key={t.slug} value={t.slug}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {showCoOwners && (
          <div className="space-y-2">
            <Label className="flex items-center gap-1 text-sm font-medium">
              Co-owner's name(s) <span className="text-secondary">(optional)</span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3.5 w-3.5 text-tertiary" />
                  </TooltipTrigger>
                  <TooltipContent>The other person(s) who co-owned this account.</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </Label>
            <Input
              placeholder="e.g., Jane Smith, Robert Johnson"
              value={coOwners}
              onChange={(e) => setCoOwners(e.target.value)}
            />
          </div>
        )}

        {showBeneficiaries && (
          <div className="space-y-3">
            <div>
              <Label className="text-sm font-medium">
                Beneficiaries <span className="text-secondary">(optional)</span>
              </Label>
              <p className="text-2xs text-secondary">
                The people or organizations {firstName} named to receive this account.
              </p>
            </div>

            <PeopleSelector
              mode="multi"
              value={beneficiaryIds}
              onChange={setBeneficiaryIds}
              placeholder="Add a beneficiary"
            />
          </div>
        )}
      </div>

      <div className="flex flex-col items-center gap-3">
        <Button
          variant="brand"
          rounded
          className="w-[400px] px-6 py-2 gap-2"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Adding..." : "Continue"}
        </Button>
        <Button
          variant="link"
          className="text-secondary"
          onClick={() => goToScreen("review_accounts")}
        >
          I'll do this later
        </Button>
      </div>
    </div>
  );
};
